import { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { generateReferralCode } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2, ArrowLeft, ArrowRight, Check } from 'lucide-react';

const MARKETING_CHANNELS = [
  { id: 'email', label: 'Email Marketing' },
  { id: 'social_media', label: 'Social Media' },
  { id: 'blog', label: 'Blog/Content Marketing' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'podcast', label: 'Podcast' },
  { id: 'paid_ads', label: 'Paid Advertising' },
  { id: 'seo', label: 'SEO/Organic Search' },
  { id: 'events', label: 'Events/Conferences' },
  { id: 'referrals', label: 'Word of Mouth/Referrals' },
];

const AUDIENCE_SIZES = [
  { value: 'under_1k', label: 'Under 1,000' },
  { value: '1k_10k', label: '1,000 - 10,000' },
  { value: '10k_50k', label: '10,000 - 50,000' },
  { value: '50k_100k', label: '50,000 - 100,000' },
  { value: 'over_100k', label: 'Over 100,000' },
];

const EXPERIENCE_LEVELS = [
  { value: 'none', label: 'No experience' },
  { value: 'beginner', label: 'Beginner (1-2 programs)' },
  { value: 'intermediate', label: 'Intermediate (3-5 programs)' },
  { value: 'expert', label: 'Expert (5+ programs)' },
];

export default function ApplyPage() {
  const { user, userData, refreshUserData, isSuperAdmin, isPartner, partner, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // ALL hooks must be declared at the top, before any conditional returns
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state - must be before any early returns to comply with Rules of Hooks
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    audienceSize: '',
    primaryAudience: '',
    marketingChannels: [] as string[],
    instagram: '',
    twitter: '',
    linkedin: '',
    youtube: '',
    tiktok: '',
    affiliateExperience: '',
    otherAffiliatePrograms: '',
    interestedProducts: [] as string[],
    promotionStrategy: '',
    additionalNotes: '',
    agreedToTerms: false,
  });

  // Field-level validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Set email from user when available (after auth loads)
  useEffect(() => {
    if (user?.email && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email || '' }));
    }
  }, [user?.email, formData.email]);

  // Show loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Super admins should go to dashboard
  if (isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  // Approved partners should go to dashboard
  if (isPartner && partner?.status === 'approved') {
    return <Navigate to="/" replace />;
  }

  // Pending partners should go to pending page
  if (isPartner && partner?.status === 'pending') {
    return <Navigate to="/pending-approval" replace />;
  }

  // If user is already marked as partner in userData, redirect to dashboard
  // (handles case where partner doc might not be loaded yet)
  if (userData?.isPartner) {
    return <Navigate to="/" replace />;
  }

  const updateFormData = (field: string, value: string | string[] | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleChannel = (channelId: string) => {
    setFormData((prev) => ({
      ...prev,
      marketingChannels: prev.marketingChannels.includes(channelId)
        ? prev.marketingChannels.filter((c) => c !== channelId)
        : [...prev.marketingChannels, channelId],
    }));
  };

  const toggleProduct = (product: string) => {
    setFormData((prev) => ({
      ...prev,
      interestedProducts: prev.interestedProducts.includes(product)
        ? prev.interestedProducts.filter((p) => p !== product)
        : [...prev.interestedProducts, product],
    }));
  };

  // Validation helpers
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidUrl = (url: string): boolean => {
    if (!url) return true; // Optional fields can be empty
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };

  const isValidPhone = (phone: string): boolean => {
    if (!phone) return true; // Optional field
    // Allow various phone formats: +1234567890, (123) 456-7890, 123-456-7890, etc.
    const phoneRegex = /^[\d\s\-\+\(\)\.]{7,20}$/;
    return phoneRegex.test(phone);
  };

  const validateField = (field: string, value: string): string | null => {
    switch (field) {
      case 'email':
        if (!value) return 'Email is required';
        if (!isValidEmail(value)) return 'Please enter a valid email address';
        break;
      case 'phone':
        if (value && !isValidPhone(value)) return 'Please enter a valid phone number';
        break;
      case 'website':
        if (value && !isValidUrl(value)) return 'Please enter a valid URL (e.g., https://example.com)';
        break;
      case 'instagram':
      case 'twitter':
      case 'linkedin':
      case 'youtube':
      case 'tiktok':
        if (value && !isValidUrl(value)) return 'Please enter a valid URL';
        break;
    }
    return null;
  };

  const handleFieldBlur = (field: string, value: string) => {
    const error = validateField(field, value);
    setFieldErrors(prev => ({
      ...prev,
      [field]: error || '',
    }));
  };

  const validateStep = (stepNum: number): boolean => {
    switch (stepNum) {
      case 1:
        const hasRequiredFields = !!(formData.firstName && formData.lastName && formData.email);
        const hasValidEmail = isValidEmail(formData.email);
        const hasValidPhone = isValidPhone(formData.phone);
        const hasValidWebsite = isValidUrl(formData.website);
        return hasRequiredFields && hasValidEmail && hasValidPhone && hasValidWebsite;
      case 2:
        const hasStep2Required = !!(formData.audienceSize && formData.primaryAudience && formData.marketingChannels.length > 0);
        const hasValidSocials = [formData.instagram, formData.twitter, formData.linkedin, formData.youtube, formData.tiktok]
          .every(url => isValidUrl(url));
        return hasStep2Required && hasValidSocials;
      case 3:
        return !!(formData.affiliateExperience && formData.interestedProducts.length > 0 && formData.promotionStrategy);
      case 4:
        return formData.agreedToTerms;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to apply');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const partnerId = user.uid;
      const referralCode = generateReferralCode();

      // Create partner document
      await setDoc(doc(db, 'partners', partnerId), {
        userId: user.uid,
        email: formData.email,
        name: `${formData.firstName} ${formData.lastName}`,
        company: formData.company || null,
        website: formData.website || null,
        status: 'pending',
        application: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          company: formData.company || null,
          website: formData.website || null,
          audienceSize: formData.audienceSize,
          primaryAudience: formData.primaryAudience,
          marketingChannels: formData.marketingChannels,
          socialMediaLinks: {
            instagram: formData.instagram || null,
            twitter: formData.twitter || null,
            linkedin: formData.linkedin || null,
            youtube: formData.youtube || null,
            tiktok: formData.tiktok || null,
          },
          affiliateExperience: formData.affiliateExperience,
          otherAffiliatePrograms: formData.otherAffiliatePrograms || null,
          interestedProducts: formData.interestedProducts,
          promotionStrategy: formData.promotionStrategy,
          additionalNotes: formData.additionalNotes || null,
          agreedToTerms: true,
          agreedToTermsAt: new Date(),
        },
        referralCode,
        defaultCommissionRate: 0.20, // 20% default
        payoutThreshold: 50,
        stats: {
          totalClicks: 0,
          totalSignups: 0,
          totalConversions: 0,
          totalEarnings: 0,
          pendingBalance: 0,
          paidOut: 0,
          conversionRate: 0,
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Update user document
      await updateDoc(doc(db, 'users', user.uid), {
        isPartner: true,
        partnerId,
        updatedAt: serverTimestamp(),
      });

      await refreshUserData();
      navigate('/pending-approval');
    } catch (err) {
      console.error('Application error:', err);
      setError('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-bold">C</span>
              </div>
              <div>
                <span className="font-semibold text-xl">cannalytix</span>
                <span className="text-sm text-muted-foreground ml-1">partners</span>
              </div>
            </div>
          </div>
          <h1 className="text-2xl font-bold">Partner Application</h1>
          <p className="text-muted-foreground mt-2">
            Join our affiliate program and earn commissions on referrals
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    s < step
                      ? 'bg-primary text-primary-foreground'
                      : s === step
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s < step ? <Check className="h-4 w-4" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 h-1 mx-1 ${s < step ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Tell us about yourself</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input id="firstName" value={formData.firstName} onChange={(e) => updateFormData('firstName', e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input id="lastName" value={formData.lastName} onChange={(e) => updateFormData('lastName', e.target.value)} required />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                  onBlur={(e) => handleFieldBlur('email', e.target.value)}
                  className={fieldErrors.email ? 'border-red-500' : ''}
                  required
                />
                {fieldErrors.email && <p className="text-sm text-red-500">{fieldErrors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  onBlur={(e) => handleFieldBlur('phone', e.target.value)}
                  className={fieldErrors.phone ? 'border-red-500' : ''}
                  placeholder="(123) 456-7890"
                />
                {fieldErrors.phone && <p className="text-sm text-red-500">{fieldErrors.phone}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="company">Company (optional)</Label>
                <Input id="company" value={formData.company} onChange={(e) => updateFormData('company', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">Website (optional)</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  value={formData.website}
                  onChange={(e) => updateFormData('website', e.target.value)}
                  onBlur={(e) => handleFieldBlur('website', e.target.value)}
                  className={fieldErrors.website ? 'border-red-500' : ''}
                />
                {fieldErrors.website && <p className="text-sm text-red-500">{fieldErrors.website}</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Audience & Marketing */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Audience & Marketing</CardTitle>
              <CardDescription>Tell us about your reach and marketing channels</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Audience Size *</Label>
                <Select value={formData.audienceSize} onValueChange={(v) => updateFormData('audienceSize', v)}>
                  <SelectTrigger><SelectValue placeholder="Select audience size" /></SelectTrigger>
                  <SelectContent>
                    {AUDIENCE_SIZES.map((size) => (<SelectItem key={size.value} value={size.value}>{size.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryAudience">Primary Audience *</Label>
                <Input id="primaryAudience" placeholder="e.g., Cannabis dispensary owners, CBD retailers" value={formData.primaryAudience} onChange={(e) => updateFormData('primaryAudience', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Marketing Channels * (select all that apply)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {MARKETING_CHANNELS.map((channel) => (
                    <div key={channel.id} className="flex items-center space-x-2">
                      <Checkbox id={channel.id} checked={formData.marketingChannels.includes(channel.id)} onCheckedChange={() => toggleChannel(channel.id)} />
                      <Label htmlFor={channel.id} className="text-sm font-normal cursor-pointer">{channel.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Social Media Links (optional)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Input
                      placeholder="Instagram URL"
                      value={formData.instagram}
                      onChange={(e) => updateFormData('instagram', e.target.value)}
                      onBlur={(e) => handleFieldBlur('instagram', e.target.value)}
                      className={fieldErrors.instagram ? 'border-red-500' : ''}
                    />
                    {fieldErrors.instagram && <p className="text-xs text-red-500 mt-1">{fieldErrors.instagram}</p>}
                  </div>
                  <div>
                    <Input
                      placeholder="Twitter/X URL"
                      value={formData.twitter}
                      onChange={(e) => updateFormData('twitter', e.target.value)}
                      onBlur={(e) => handleFieldBlur('twitter', e.target.value)}
                      className={fieldErrors.twitter ? 'border-red-500' : ''}
                    />
                    {fieldErrors.twitter && <p className="text-xs text-red-500 mt-1">{fieldErrors.twitter}</p>}
                  </div>
                  <div>
                    <Input
                      placeholder="LinkedIn URL"
                      value={formData.linkedin}
                      onChange={(e) => updateFormData('linkedin', e.target.value)}
                      onBlur={(e) => handleFieldBlur('linkedin', e.target.value)}
                      className={fieldErrors.linkedin ? 'border-red-500' : ''}
                    />
                    {fieldErrors.linkedin && <p className="text-xs text-red-500 mt-1">{fieldErrors.linkedin}</p>}
                  </div>
                  <div>
                    <Input
                      placeholder="YouTube URL"
                      value={formData.youtube}
                      onChange={(e) => updateFormData('youtube', e.target.value)}
                      onBlur={(e) => handleFieldBlur('youtube', e.target.value)}
                      className={fieldErrors.youtube ? 'border-red-500' : ''}
                    />
                    {fieldErrors.youtube && <p className="text-xs text-red-500 mt-1">{fieldErrors.youtube}</p>}
                  </div>
                  <div>
                    <Input
                      placeholder="TikTok URL"
                      value={formData.tiktok}
                      onChange={(e) => updateFormData('tiktok', e.target.value)}
                      onBlur={(e) => handleFieldBlur('tiktok', e.target.value)}
                      className={fieldErrors.tiktok ? 'border-red-500' : ''}
                    />
                    {fieldErrors.tiktok && <p className="text-xs text-red-500 mt-1">{fieldErrors.tiktok}</p>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Experience & Strategy */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Experience & Strategy</CardTitle>
              <CardDescription>Tell us about your affiliate experience and promotion plans</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Affiliate Marketing Experience *</Label>
                <Select value={formData.affiliateExperience} onValueChange={(v) => updateFormData('affiliateExperience', v)}>
                  <SelectTrigger><SelectValue placeholder="Select experience level" /></SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_LEVELS.map((level) => (<SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otherPrograms">Other Affiliate Programs (optional)</Label>
                <Input id="otherPrograms" placeholder="List any other affiliate programs you're part of" value={formData.otherAffiliatePrograms} onChange={(e) => updateFormData('otherAffiliatePrograms', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Products You Want to Promote *</Label>
                <div className="flex gap-4 mt-2">
                  {[{ id: 'cannalytix', label: 'Cannalytix (Link Tracking)' }, { id: 'grobot', label: 'Grobot (AI Chat)' }, { id: 'both', label: 'Both Products' }].map((product) => (
                    <div key={product.id} className="flex items-center space-x-2">
                      <Checkbox id={product.id} checked={formData.interestedProducts.includes(product.id)} onCheckedChange={() => toggleProduct(product.id)} />
                      <Label htmlFor={product.id} className="text-sm font-normal cursor-pointer">{product.label}</Label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="strategy">How do you plan to promote our products? *</Label>
                <Textarea id="strategy" placeholder="Describe your promotion strategy..." value={formData.promotionStrategy} onChange={(e) => updateFormData('promotionStrategy', e.target.value)} rows={4} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes (optional)</Label>
                <Textarea id="notes" placeholder="Anything else you'd like us to know?" value={formData.additionalNotes} onChange={(e) => updateFormData('additionalNotes', e.target.value)} rows={3} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Review & Submit */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Review & Submit</CardTitle>
              <CardDescription>Review your application and agree to our terms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{formData.firstName} {formData.lastName}</span></div>
                <div><span className="text-muted-foreground">Email:</span> <span className="font-medium">{formData.email}</span></div>
                {formData.company && <div><span className="text-muted-foreground">Company:</span> <span className="font-medium">{formData.company}</span></div>}
                <div><span className="text-muted-foreground">Audience Size:</span> <span className="font-medium">{AUDIENCE_SIZES.find(s => s.value === formData.audienceSize)?.label}</span></div>
                <div><span className="text-muted-foreground">Products:</span> <span className="font-medium">{formData.interestedProducts.join(', ')}</span></div>
              </div>
              <div className="border rounded-lg p-4 bg-card">
                <h4 className="font-medium mb-2">Partner Program Terms</h4>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Commissions are paid monthly for approved referrals</li>
                  <li>Minimum payout threshold is $50</li>
                  <li>You agree not to use spam or misleading marketing tactics</li>
                  <li>Cannalytix reserves the right to terminate partnerships for violations</li>
                </ul>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox id="terms" checked={formData.agreedToTerms} onCheckedChange={(checked) => updateFormData('agreedToTerms', checked)} />
                <Label htmlFor="terms" className="text-sm font-normal cursor-pointer">I agree to the Partner Program Terms and Conditions and understand that my application will be reviewed before approval.</Label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation buttons will be added in the next edit */}
        <div className="flex justify-between mt-6">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(step - 1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          ) : (
            <Link to="/login">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Login
              </Button>
            </Link>
          )}
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)} disabled={!validateStep(step)}>
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading || !validateStep(step)}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Application
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

